import * as React from 'react'
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  useTheme,
  alpha,
  Stack
} from '@mui/material'
import {
  Close as CloseIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material'
import { WhatsappIcon, WhatsappShareButton, EmailIcon, EmailShareButton } from 'react-share'

const ShareProfileDialog = ({ open, handleClose, tutorId, tutorName }) => {
  const theme = useTheme()
  const [copied, setCopied] = React.useState(false)
  const whatsappShareRef = React.useRef(null)
  const emailShareRef = React.useRef(null)

  const profileURL = `${window.location.origin}/tutor/${tutorId}`

  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(profileURL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const handleWhatsAppShare = () => {
    if (whatsappShareRef.current) {
      whatsappShareRef.current.click()
    }
  }

  const handleEmailShare = () => {
    if (emailShareRef.current) {
      emailShareRef.current.click()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Share Tutor Profile
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.08)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ 
          color: theme.palette.text.secondary,
          my: 3,
          textAlign: 'center'
        }}>
          Share {tutorName}'s profile with others
        </Typography>

        {/* Share Options */}
        <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 3 }}>
          {/* Copy Link */}
          <Box
            onClick={copyProfileLink}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s ease',
              minWidth: 100,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                transition: 'all 0.2s ease'
              }}
            >
              {copied ? (
                <CopyIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
              ) : (
                <LinkIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ 
              fontWeight: 500,
              color: copied ? theme.palette.success.main : theme.palette.text.primary,
              textAlign: 'center'
            }}>
              {copied ? 'Copied!' : 'Copy Link'}
            </Typography>
          </Box>

          {/* WhatsApp */}
          <Box
            onClick={handleWhatsAppShare}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s ease',
              minWidth: 100,
              '&:hover': {
                borderColor: '#25D366',
                backgroundColor: alpha('#25D366', 0.04),
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha('#25D366', 0.15)}`
              }
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: alpha('#25D366', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1
              }}
            >
              <WhatsappIcon size={24} round style={{ color: '#25D366' }} />
            </Box>
            <Typography variant="body2" sx={{ 
              fontWeight: 500,
              color: theme.palette.text.primary,
              textAlign: 'center'
            }}>
              WhatsApp
            </Typography>
            <WhatsappShareButton
              url={profileURL}
              ref={whatsappShareRef}
              title={`Check out ${tutorName}'s tutor profile`}
              style={{ display: 'none' }}
            />
          </Box>

          {/* Email */}
          <Box
            onClick={handleEmailShare}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s ease',
              minWidth: 100,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1
              }}
            >
              <EmailIcon size={24} round style={{ color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="body2" sx={{ 
              fontWeight: 500,
              color: theme.palette.text.primary,
              textAlign: 'center'
            }}>
              Email
            </Typography>
            <EmailShareButton
              url={profileURL}
              ref={emailShareRef}
              subject={`${tutorName} - Tutor Profile`}
              body={`Check out ${tutorName}'s tutor profile: `}
              style={{ display: 'none' }}
            />
          </Box>
        </Stack>

        {/* URL Display */}
        <Box
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="body2" sx={{ 
            color: theme.palette.text.secondary,
            mb: 1,
            fontWeight: 500
          }}>
            Profile URL:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.primary.main,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              fontSize: '0.875rem'
            }}
          >
            {profileURL}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShareProfileDialog